import { useState } from "react";
import { Pencil, Clock, Hash } from "lucide-react";
import { Spinner, EmptyState } from "../ui";
import Drawer from "../ui/Drawer";
import { useRelated, type ResourceConfig } from "../../lib/admin/resources";
import { useAudit } from "../../lib/queries";
import { canWrite } from "../../lib/admin/resources";
import { useAuth } from "../../lib/auth";
import { fmtTime, timeAgo } from "../../lib/format";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Rec = Record<string, any>;

export default function ResourceDetail({
  config, record, onClose, onEdit,
}: {
  config: ResourceConfig;
  record: Rec;
  onClose: () => void;
  onEdit: (r: Rec) => void;
}) {
  const { capabilities } = useAuth();
  const writable = canWrite(config, capabilities) && !config.noEdit;
  const tabs = ["Details", ...(config.relations?.map((r) => r.label) ?? []), "Audit"];
  const [tab, setTab] = useState("Details");

  return (
    <Drawer
      onClose={onClose}
      width="max-w-[560px]"
      eyebrow={`${config.singular.toUpperCase()} · #${record.id}`}
      title={String(record[config.titleField] ?? `#${record.id}`)}
      headerRight={
        writable ? (
          <button onClick={() => onEdit(record)} className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs hover:border-aurora-cyan/40">
            <Pencil size={13} /> Edit
          </button>
        ) : undefined
      }
    >
      {/* tabs */}
      <div className="flex gap-1 border-b border-white/10 px-3">
        {tabs.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-2 text-xs transition ${tab === t ? "border-b-2 border-aurora-cyan text-aurora-cyan" : "text-slatey hover:text-ink"}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="p-4">
        {tab === "Details" && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {config.columns.map((c) => (
              <div key={c.key} className="rounded-lg border border-white/5 bg-white/5 p-2">
                <div className="section-tag">{c.label}</div>
                <div className="mt-0.5 text-xs">{c.render ? c.render(record) : String(record[c.key] ?? "—")}</div>
              </div>
            ))}
          </div>
        )}

        {config.relations?.map((rel) =>
          tab === rel.label ? <RelatedTab key={rel.label} rel={rel} id={record.id} /> : null
        )}

        {tab === "Audit" && <AuditTab objectType={config.auditLabel} objectId={record.id} />}
      </div>
    </Drawer>
  );
}

function RelatedTab({ rel, id }: { rel: NonNullable<ResourceConfig["relations"]>[number]; id: number }) {
  const { data, isLoading } = useRelated(rel.endpoint, rel.filterParam, id);
  const rows: Rec[] = data?.results ?? [];
  if (isLoading) return <Spinner />;
  if (rows.length === 0) return <EmptyState>No related {rel.label.toLowerCase()}.</EmptyState>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-white/10 text-left text-slatey">
            {rel.columns.map((c) => <th key={c.key} className="px-2 py-1.5 font-medium uppercase tracking-wider">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-white/5">
              {rel.columns.map((c) => <td key={c.key} className="px-2 py-1.5">{c.render ? c.render(r) : String(r[c.key] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditTab({ objectType, objectId }: { objectType?: string; objectId: number }) {
  const { data, isLoading } = useAudit({ objectType, objectId });
  if (isLoading) return <Spinner />;
  const entries = data?.entries ?? [];
  if (entries.length === 0)
    return <EmptyState>No audit entries for this record yet.</EmptyState>;
  return (
    <div className="space-y-2">
      {entries.map((e) => (
        <div key={e.id} className="rounded-lg border border-white/5 bg-white/5 p-2.5 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-medium text-aurora-cyan">{e.action}</span>
            <span className="flex items-center gap-1 text-slatey"><Clock size={11} /> {timeAgo(e.created_at)}</span>
          </div>
          <div className="mt-1 text-slatey">by {e.actor} · {fmtTime(e.created_at)}</div>
          <div className="mono mt-1 flex items-center gap-1 text-[9px] text-slate-600"><Hash size={9} />{e.hash.slice(0, 16)}</div>
        </div>
      ))}
    </div>
  );
}
